import { purgeMediaObjects } from "../api";
import { supabase } from "../supabase";
import { chunk, DAY_MS, RETENTION_DAYS } from "./retention";

/** 1回の物理削除で送るキー数の上限(Worker の MAX_PURGE_KEYS に合わせる) */
const PURGE_CHUNK = 40;

/** 1度に掃除する件数。ゴミ箱を開くたびに走るので、待たせない範囲に抑える */
const PURGE_BATCH = 20;

export interface TrashItem {
  id: string;
  type: "photo" | "video";
  thumbnail_key: string;
  storage_key: string;
  deleted_at: string;
}

const COLUMNS = "id, type, thumbnail_key, storage_key, deleted_at";

/** ゴミ箱の中身。削除が新しい順 */
export async function fetchDeletedMedia(): Promise<TrashItem[]> {
  const { data, error } = await supabase
    .from("media")
    .select(COLUMNS)
    .not("deleted_at", "is", null)
    .order("deleted_at", { ascending: false });
  if (error) throw error;
  const items: TrashItem[] = data;
  return items;
}

/** 削除を取り消してタイムラインに戻す */
export async function restoreMedia(mediaIds: string[]): Promise<void> {
  if (mediaIds.length === 0) return;
  const { error } = await supabase.from("media").update({ deleted_at: null }).in("id", mediaIds);
  if (error) throw error;
}

/**
 * 物理削除する。取り消せない。
 *
 * **R2 を先に消し、DB の行は後に消す。** 逆にすると、行が消えた後に R2 の削除が
 * 失敗したとき、そのオブジェクトを指す手掛かりがどこにも残らず永久に残骸になる。
 * この順序なら、途中で落ちても行が残るので次回のゴミ箱表示でやり直せる。
 */
export async function purgeMedia(items: TrashItem[]): Promise<void> {
  if (items.length === 0) return;

  const keys = items.flatMap((item) => [item.storage_key, item.thumbnail_key]);
  for (const part of chunk(keys, PURGE_CHUNK)) {
    await purgeMediaObjects(part);
  }

  const { error } = await supabase
    .from("media")
    .delete()
    .in(
      "id",
      items.map((item) => item.id),
    );
  if (error) throw error;
}

/**
 * 保持期間を過ぎたものを物理削除し、消した件数を返す。
 *
 * 定期実行ではなくゴミ箱を開いたときに走らせる(→ ADR-0015)。
 * 誰も開かなければ残り続けるが、二人だけのアプリでは容量として無視できる。
 */
export async function purgeExpired(now: Date = new Date()): Promise<number> {
  const cutoff = new Date(now.getTime() - RETENTION_DAYS * DAY_MS).toISOString();

  // 期限切れは古い方に溜まる。一覧とは別に、古い順で必要な分だけ引く
  const { data, error } = await supabase
    .from("media")
    .select(COLUMNS)
    .not("deleted_at", "is", null)
    .lt("deleted_at", cutoff)
    .order("deleted_at", { ascending: true })
    .limit(PURGE_BATCH);
  if (error) throw error;

  const expired: TrashItem[] = data;
  await purgeMedia(expired);
  return expired.length;
}
